import { HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { jwtDecode } from 'jwt-decode';
import { map, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LocalStorageService } from '../../@core/services/local-storage.service';
import { User } from '../../@core/services/session-manager.service';
import { CONFIG } from '../../config';
import { SafeAny } from '../../types/safe-any';
import { ApiErrorType, ErrorsEmail } from '../app.actions';

import { UserApiActions } from './user.actions';
import { UserService } from './user.service';
import { UserDetails } from './user.state.interface';

export const login = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.login),
      switchMap(({ payload }) =>
        userService.login(payload).pipe(
          map((response) =>
            UserApiActions.loginSuccess({
              payload: response,
            }),
          ),
          catchError((e: ApiErrorType) => of(UserApiActions.loginError({ payload: e }))),
        ),
      ),
    ),
  { functional: true },
);

export const loginSuccess = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    localStorageService = inject(LocalStorageService),
  ) =>
    actions$.pipe(
      ofType(UserApiActions.loginSuccess),
      tap(({ payload }) => {
        const decodedToken = jwtDecode(payload.token) as Partial<User>;

        localStorageService.setItem('token', payload.token);
        localStorageService.setItem('id', decodedToken.id);
        localStorageService.setItem('firstName', decodedToken.firstName);
        localStorageService.setItem('lastName', decodedToken.lastName);
        localStorageService.setItem('roles', decodedToken.roles);

        console.log('added comment to test github setup');

        router.navigate(['/']).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

export const loginError = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(UserApiActions.loginError),
      tap(({ payload }) => {
        snackBar.open(
          (payload.error?.errors as ErrorsEmail)?.email ||
            payload.message ||
            'Please enter a valid email and password combination.',
          'Close',
          {
            duration: 5000,
            panelClass: ['snackbar-error'],
            verticalPosition: 'bottom',
          },
        );
      }),
    ),
  { functional: true, dispatch: false },
);

export const logout = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(UserApiActions.logout),
      tap(() => {
        localStorage.clear();

        router.navigate(['/auth/login']);
      }),
    ),
  { functional: true, dispatch: false },
);

export const changePasswordSuccess = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    localStorageService = inject(LocalStorageService),
  ) =>
    actions$.pipe(
      ofType(UserApiActions.changePasswordSuccess),
      tap(() => {
        localStorageService.clear();

        router.navigate(['/auth/login']);
      }),
    ),
  { functional: true, dispatch: false },
);

export const loadLocalStorage = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(UserApiActions.loadLocalStorage),
      switchMap(() => {
        const firstName = localStorage.getItem('firstName') || '';
        const id = localStorage.getItem('id') || '';
        const lastName = localStorage.getItem('lastName') || '';
        const roles = localStorage.getItem('roles') || '';
        const token = localStorage.getItem('token') || '';
        const username = localStorage.getItem('username') || '';

        const payload = {
          firstName,
          id,
          lastName,
          roles,
          token,
          username,
        };

        return of(UserApiActions.loadLocalStorageSuccess({ payload }));
      }),
    ),
  { functional: true },
);

// Add user
export const addUser = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.addUser),
      switchMap(({ payload }: SafeAny) =>
        userService.addUser(payload).pipe(
          map((addedUser: UserDetails) => UserApiActions.addUserSuccess({ addedUser })),
          catchError((error: ApiErrorType) => of(UserApiActions.addUserError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Navigate to 'users/:id/view' after user is successfully created
export const addUserSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(UserApiActions.addUserSuccess),
      tap(({ addedUser }) => {
        snackBar.open('User successfully added.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate([`/users/${addedUser.id}/view`]).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

// Add new effects for active users
export const fetchActiveUsers = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.fetchActiveUsers),
      switchMap(({ payload }: SafeAny) =>
        userService.getActiveUsers(payload).pipe(
          map((response: HttpResponse<UserDetails[]>) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const users = response.body || [];

            return UserApiActions.fetchActiveUsersSuccess({ users, totalCount: +totalCount });
          }),
          catchError((error: ApiErrorType) => of(UserApiActions.fetchActiveUsersError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Add new effects for archived users
export const fetchArchivedUsers = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.fetchArchivedUsers),
      switchMap(({ payload }: SafeAny) =>
        userService.getArchivedUsers(payload).pipe(
          map((response: HttpResponse<UserDetails[]>) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const users = response.body || [];

            return UserApiActions.fetchArchivedUsersSuccess({ users, totalCount: +totalCount });
          }),
          catchError((error: ApiErrorType) =>
            of(UserApiActions.fetchArchivedUsersError({ error })),
          ),
        ),
      ),
    ),
  { functional: true },
);

// Add new effects for archiving users
export const archiveUser = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.archiveUser),
      switchMap(({ userId }) =>
        userService.archiveUser(userId, 'INACTIVE').pipe(
          map((archivedUser: UserDetails) => UserApiActions.archiveUserSuccess({ archivedUser })),
          catchError((error: ApiErrorType) => of(UserApiActions.archiveUserError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Add effects for successful archive and redirect to users/archived
export const archiveUserSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(UserApiActions.archiveUserSuccess),
      tap(() => {
        snackBar.open('User successfully archived.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate(['/users/archived']).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

// Add new effects for unarchiving users and redirect to users/active
export const unarchiveUser = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.unarchiveUser),
      switchMap(({ userId }) =>
        userService.unarchiveUser(userId, 'ACTIVE').pipe(
          map((unarchivedUser: UserDetails) =>
            UserApiActions.unarchiveUserSuccess({ unarchivedUser }),
          ),
          catchError((error: ApiErrorType) => of(UserApiActions.unarchiveUserError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Add effects for successful unarchive to refresh archived users list
export const unarchiveUserSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(UserApiActions.unarchiveUserSuccess),
      tap(() => {
        snackBar.open('User successfully unarchived.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate(['/users/active']).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

// Get Specific User
export const fetchUser = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.fetchUser),
      switchMap(({ userId }) =>
        userService.getUser(userId).pipe(
          map((user: UserDetails) => UserApiActions.fetchUserSuccess({ user })),
          catchError((error: ApiErrorType) => of(UserApiActions.fetchUserError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Add new effects for user update
export const updateUser = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.updateUser),
      switchMap(({ payload, userId }) =>
        userService.updateUser(payload, userId).pipe(
          map((updatedUser: UserDetails) => UserApiActions.updateUserSuccess({ updatedUser })),
          catchError((error: ApiErrorType) => of(UserApiActions.updateUserError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Get User Roles
export const fetchUserRoles = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.fetchUserRoles),
      switchMap(({ payload }) =>
        userService.getUserRoles(payload).pipe(
          map((roles) => UserApiActions.fetchUserRolesSuccess({ roles })),
          catchError((error: ApiErrorType) => of(UserApiActions.fetchUserRolesError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Navigate to 'users/:id/view' after user is successfully updated
export const updateUserSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(UserApiActions.updateUserSuccess),
      tap(({ updatedUser }) => {
        const roles = localStorage.getItem('roles');

        if (roles === CONFIG.ROLES.SUPER_ADMIN || roles === CONFIG.ROLES.ADMIN) {
          router.navigate([`/users/${updatedUser.id}/view`]).catch(console.error);
        }
      }),
    ),
  { functional: true, dispatch: false },
);

// Reset Password
export const resetPassword = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.resetPassword),
      switchMap(({ userId }) =>
        userService.resetPassword(userId).pipe(
          map(() => UserApiActions.resetPasswordSuccess()),
          catchError((error: ApiErrorType) => of(UserApiActions.resetPasswordError({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Reset Password Success
export const resetPasswordSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(UserApiActions.resetPasswordSuccess),
      tap(() => {
        snackBar.open('Password successfully reset.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate(['/users']).catch(console.error);
      }),
    ),
  { functional: true, dispatch: false },
);

// Change Password
export const changePassword = createEffect(
  (actions$ = inject(Actions), userService = inject(UserService)) =>
    actions$.pipe(
      ofType(UserApiActions.changePassword),
      switchMap(({ payload, userId }) =>
        userService.changePassword(payload, userId).pipe(
          map((changedPassword) => UserApiActions.changePasswordSuccess({ changedPassword })),
          catchError((error: ApiErrorType) => of(UserApiActions.changePasswordError({ error }))),
        ),
      ),
    ),
  { functional: true },
);
